import { ArrowCircleDown, ArrowCircleUp } from "@phosphor-icons/react";
import clsx from "clsx";
import React from "react";
import Typography from "./Typography";

export type TableHeaderConfig = {
    label: string;
    orderByKey?: string;
};

export type TableProps<T> = {
    data: T[];
    headings: TableHeaderConfig[];
    renderRowCells: (rowData: T) => React.ReactNode;
    onRowClick?: (rowData: T) => void;
    orderBy?: string;
    sortOrder?: string;
    onOrderBy?: (orderBy: string) => void;
    className?: string;
};

const Table = <T extends { id: number | string }>({
    data,
    headings,
    renderRowCells,
    onRowClick,
    orderBy,
    sortOrder,
    onOrderBy = () => null,
    className,
}: TableProps<T>) => {
    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLTableRowElement>,
        rowData: T
    ) => {
        if (!onRowClick) return;

        if (e.key === "Enter" || e.key === " ") {
            onRowClick(rowData);
        }
    };

    return (
        <div className="overflow-x-auto grow">
            <table
                className={clsx(
                    "overflow-hidden w-full text-left border-separate rounded-xl border border-surface-light-5 border-spacing-0 min-w-[1000px]",
                    className
                )}
            >
                <thead className="bg-deep-ocean-20 text-white">
                    <tr className="child:px-4 child:py-2 sm:child:py-4 rounded-xl">
                        {headings.map((heading) => (
                            <th
                                key={heading.label}
                                className={clsx(
                                    "max-w-0 ",
                                    heading.orderByKey === "ActivityDate" &&
                                        "min-w-[80px]",
                                    heading.orderByKey && "cursor-pointer"
                                )}
                                onClick={
                                    heading.orderByKey
                                        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                          () => onOrderBy(heading.orderByKey!)
                                        : () => null
                                }
                                title={heading.label}
                            >
                                <Typography
                                    variant="label-large"
                                    component="span"
                                >
                                    {heading.label}
                                </Typography>
                                {orderBy &&
                                    orderBy === heading.orderByKey &&
                                    (sortOrder === "asc" ? (
                                        <ArrowCircleUp
                                            className="ml-2 text-soft-green-80 flex-shrink-0 inline"
                                            weight="fill"
                                            size={16}
                                        />
                                    ) : (
                                        <ArrowCircleDown
                                            className="ml-2 text-soft-green-80 flex-shrink-0 inline"
                                            weight="fill"
                                            size={16}
                                        />
                                    ))}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="overflow-hidden">
                    {data.map((rowData) => (
                        <tr
                            key={rowData.id}
                            className={clsx(
                                "child:px-4 child:py-4 text-sm break-all child:border-b child:last-of-type:border-0 transition-colors child:max-w-0 child:overflow-hidden child:text-ellipsis child:whitespace-nowrap",
                                onRowClick &&
                                    "cursor-pointer select-none hover:bg-surface-light-1"
                            )}
                            onClick={
                                onRowClick
                                    ? () => onRowClick(rowData)
                                    : undefined
                            }
                            onKeyDown={(e) => handleKeyDown(e, rowData)}
                            tabIndex={onRowClick ? 0 : undefined}
                        >
                            {renderRowCells(rowData)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default Table;
